import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/shared/services/supabase';
import type { Manufacturer } from '@/shared/types/domain.types';

interface ManufacturerFilterProps {
  selectedManufacturerIds: string[];
  onChange: (manufacturerIds: string[]) => void;
  multiple?: boolean;
}

export function ManufacturerFilter({
  selectedManufacturerIds,
  onChange,
  multiple = false,
}: ManufacturerFilterProps) {
  const { data: manufacturers, isLoading } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Manufacturer[];
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (multiple) {
      const selectedOptions = Array.from(e.target.selectedOptions).map((opt) => opt.value);
      onChange(selectedOptions);
    } else {
      onChange(e.target.value ? [e.target.value] : []);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading manufacturers...</div>;
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Manufacturer
      </label>
      <select
        multiple={multiple}
        value={multiple ? selectedManufacturerIds : selectedManufacturerIds[0] || ''}
        onChange={handleChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {!multiple && <option value="">All Manufacturers</option>}
        {manufacturers?.map((manufacturer) => (
          <option key={manufacturer.id} value={manufacturer.id}>
            {manufacturer.name}
          </option>
        ))}
      </select>
      {multiple && (
        <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
      )}
    </div>
  );
}
